import { useCallback } from 'react'
import { Platform } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { buildPublicSignInHref } from '../../signIn/helpers/postAuthRedirect.helper'
import {
  LANDING_SITE_NAV_ROUTES,
  LANDING_SITE_NAV_SCROLL_OFFSET_PX,
} from '../constants/landingSiteNav.constants'
import { scrollWebWindowToElement } from '../helpers/scrollWebMarketingSection.helper'

export interface UseMarketingFooterNavigationOptions {
  /** On `/`, scroll to the in-page contact section instead of routing to `/contact`. */
  contactMode?: 'scroll' | 'route'
}

/**
 * Shared footer + legal link handlers for public marketing surfaces.
 */
export function useMarketingFooterNavigation(
  options?: UseMarketingFooterNavigationOptions,
) {
  const router = useRouter()
  const { isSignedIn } = useSupabaseAuth()
  const contactMode = options?.contactMode ?? 'route'

  const onPressPrivacy = useCallback(() => {
    router.push('/privacy')
  }, [router])

  const onPressTerms = useCallback(() => {
    router.push('/terms')
  }, [router])

  const onPressDataDeletion = useCallback(() => {
    router.push('/data-deletion')
  }, [router])

  const onPressCredits = useCallback(() => {
    router.push('/credits')
  }, [router])

  const onPressPrivacyControls = useCallback(() => {
    if (isSignedIn) {
      router.push('/privacy-controls')
      return
    }
    router.push(buildPublicSignInHref({ returnUrl: '/privacy-controls' }) as Href)
  }, [router, isSignedIn])

  const onPressContact = useCallback(() => {
    if (contactMode === 'scroll' && Platform.OS === 'web') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
      return
    }
    router.push(LANDING_SITE_NAV_ROUTES.CONTACT as Href)
  }, [router, contactMode])

  const onPressFeatures = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    window.setTimeout(() => {
      scrollWebWindowToElement(
        document.getElementById('features'),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [router])

  const onPressPricing = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    window.setTimeout(() => {
      scrollWebWindowToElement(
        document.getElementById('pricing'),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [router])

  return {
    onPressPrivacy,
    onPressTerms,
    onPressDataDeletion,
    onPressCredits,
    onPressPrivacyControls,
    onPressContact,
    onPressFeatures,
    onPressPricing,
  }
}
